import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const CookieConsentBanner = () => {
  const { consent, acceptCookies, declineCookies, hasInteracted } = useCookieConsent();
  const { t } = useTranslation("common");
  const { getPath } = useLocalizedPath();

  // Don't show if user has already interacted, or if we're in a prerender snapshot
  const isPrerender =
    typeof window !== "undefined" &&
    (window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__ === true;
  if (isPrerender || hasInteracted || consent !== "pending") {
    return null;
  }


  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-3"
      >
        <div className="mx-auto max-w-3xl">
          <div className="relative rounded-xl border border-border/50 bg-background/95 backdrop-blur-xl shadow-lg overflow-hidden">
            <div className="relative px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Icon + Text */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Cookie className="w-5 h-5 text-primary flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t("cookies.description", "We use cookies to enhance your browsing experience.")}{" "}
                  <Link 
                    to={getPath("/privacy")} 
                    className="text-foreground hover:underline font-medium"
                  >
                    {t("cookies.privacyLink", "Privacy Policy")}
                  </Link>
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  onClick={declineCookies}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  {t("cookies.decline", "Decline")}
                </Button>
                <Button
                  onClick={acceptCookies}
                  size="sm"
                  className="bg-foreground hover:bg-foreground/90 text-background"
                >
                  {t("cookies.accept", "Accept")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
